import React from 'react';
import {
  Avatar,
  Button,
  FlippableCard,
  FormField,
  FormSchemaEntry,
  FormType,
  ImagePickerModal,
  LightboxMedia,
  openImagePickerModal,
  UI,
} from '@gik/ui';
import Backdrop from '@gik/ui/Backdrop';
import bemBlock from '@gik/core/utils/bemBlock';
import Form from '@gik/ui/Form/Form';
import { deleteUser, updateUser, updateUserAvatar, useUser } from '@gik/api/users/user';
import UserSettingsSkeleton from './UserSettingsSkeleton';
import sleep from '@gik/core/utils/sleep';
import Geosuggest from 'react-geosuggest';
import { logout } from '@/utils/environment';
import { useUserStore } from '@gik/core/store/UserStore';
import { optimisticUpdate } from '@gik/core/api';
import { auth } from '@gik/auth';
import dataURLtoBlob from 'blueimp-canvas-to-blob';
import withSuspense from '@gik/core/components/suspended/withSuspense';

const blockName = 'user-settings';
export const cacheBusterStringEvent = 'userImageCacheBuster';

export interface IUserSettingsProps {}

enum UserSettingsFormFieldNames {
  FirstName = 'firstName',
  LastName = 'lastName',
  EmailAddress = 'emailAddress',
  CityAndState = 'cityAndState',
}

type EditingUserFormType = {
  [UserSettingsFormFieldNames.FirstName]: string;
  [UserSettingsFormFieldNames.LastName]: string;
  [UserSettingsFormFieldNames.EmailAddress]: string;
  [UserSettingsFormFieldNames.CityAndState]: string;
};

function UserSettings() {
  const bem = bemBlock(blockName);
  const userStore = useUserStore();
  const userId = userStore.id;
  const { data: user, error: userError, mutate: mutateUser, revalidate: revalidateUser } = useUser(userId);

  const [isFlipped, setFlipped] = React.useState<boolean>(false);
  const [newAvatarPreview, setNewAvatarPreview] = React.useState(null);

  const formRef = React.useRef<FormType>(null);
  const geoSuggestRef = React.useRef<Geosuggest>(null);

  const onClickEditProfile = function (): void {
    setFlipped(true);
    setNewAvatarPreview(null);
  };

  function finishUserEditing() {
    setFlipped(false);
    setNewAvatarPreview(null);
  }

  const cancelUserEditing = function (): void {
    finishUserEditing();

    formRef.current?.resetForm();
    geoSuggestRef.current?.setState(state => ({
      ...state,
      userInput: getGeosuggestInitialInput(),
    })); // TODO: need to find a way to hook this up with the form generator's resetForm handle
  };

  const onClickChangePassword = (): void => {
    auth.resetPassword();
  };

  const onClickDeleteAccount = async function () {
    const response = await UI.confirm('Are you sure you want to delete your account?');

    if (response) {
      UI.success('Thank you for using Give InKind!', { okText: 'Go to homepage' });

      // response.then(async () => await logout()).catch(async () => await logout());

      await deleteUser(userId);

      // gives user 6 seconds to read the message, otherwise we are continuing anyway
      await sleep(6000);
      await logout();
    }
  };

  const onChangeAvatarClicked = async function () {
    openImagePickerModal({
      title: 'Edit photo',
      text: 'Upload a photo for your profile.',
      fallback: user?.avatarURL,
      onChange: setNewAvatarPreview,
    });
  };

  const saveUserInfo = async (values: EditingUserFormType): Promise<void> => {
    const transferObject: {
      firstName: string;
      lastName: string;
      emailAddress: string;
      city?: string;
      state?: string;
    } = {
      firstName: values.firstName,
      lastName: values.lastName,
      emailAddress: values.emailAddress,
    };

    if (values.cityAndState && values.cityAndState.length > 0 && values.cityAndState.split(',').length > 1) {
      const split = values.cityAndState.split(',');
      transferObject.city = split[0].trim();
      transferObject.state = split[1].trim();
    } else {
      transferObject.city = null;
      transferObject.state = 'NA';
    }

    const updatedUser = { ...user, ...transferObject };

    if (newAvatarPreview) {
      await updateUserAvatar(userId, dataURLtoBlob(newAvatarPreview));
      revalidateUser();
    }

    optimisticUpdate(updatedUser, mutateUser, () => updateUser(userId, updatedUser));

    finishUserEditing();
  };

  const getGeosuggestInitialInput = () => {
    if (!user) {
      return null;
    }
    return user.city !== null && user.city !== '' ? `${user.city}, ${user.state}` : '';
  };

  // TODO: remove duplication between signup form schema and user settings schema
  const schema: FormSchemaEntry[] = [
    {
      name: UserSettingsFormFieldNames.FirstName,
      type: 'text',
      default: user?.firstName,
      minLength: 2,
      maxLength: 127,
      required: true,
      props: {
        placeholder: 'Your First name',
      },
    },
    {
      name: UserSettingsFormFieldNames.LastName,
      type: 'text',
      default: user?.lastName,
      //required: true, // some users do not want to provide last name
      minLength: 2,
      maxLength: 127,
      props: {
        placeholder: 'Your Last name',
      },
    },
    {
      name: UserSettingsFormFieldNames.EmailAddress,
      type: 'email',
      required: true,
      default: user?.emailAddress,
      props: {
        placeholder: 'Email',
      },
    },
    {
      name: UserSettingsFormFieldNames.CityAndState,
      type: 'geosuggest',
      default: getGeosuggestInitialInput(),
      props: {
        placeholder: 'City, state',
        initialValue: getGeosuggestInitialInput(),
        ref: geoSuggestRef,
      },
    },
  ];

  const isLoading = !user;
  const isError = isLoading && userError;

  if (isLoading) {
    return <UserSettingsSkeleton />;
  }
  if (isError) {
    return <UserSettingsSkeleton />;
  }

  function handleClickUserAvatar() {
    UI.lightbox([
      {
        mediaType: LightboxMedia.IMAGE,
        url: user.avatarURL,
        caption: `${user.firstName} ${user.lastName}`.trim(),
      },
    ]);
  }

  return (
    <Backdrop isOpen={isFlipped} liftChildrenOnTrigger={true}>
      <FlippableCard
        className={bem('card-wrapper')}
        isFlipped={isFlipped}
        frontContent={
          <div className={`${bem()} ${bem('base')}`} style={{ visibility: isFlipped ? 'hidden' : 'visible' }}>
            <section className={bem('avatar-container')}>
              <Avatar
                className={bem('avatar')}
                rounded
                size={'2xxl'}
                imageUrl={newAvatarPreview || user?.avatarThumbMediumURL}
                loading={!user}
                onClick={handleClickUserAvatar}
              />
            </section>
            <section className={bem('full-name')}>
              {user.firstName} {user.lastName}
            </section>
            <section className={bem('email-address')}>{user.emailAddress}</section>
            <section className={bem('city-state')}>
              <div className="user-details-typography">{getGeosuggestInitialInput()}</div>
            </section>

            <section className={bem('buttons')}>
              <div className={bem('edit-button')} onClick={() => onClickEditProfile()}>
                Edit profile
              </div>

              <div className={bem('password-button')} onClick={() => onClickChangePassword()}>
                Change password
              </div>

              <div className={bem('logout-button')} onClick={() => logout()}>
                Logout
              </div>

              <div className={bem('delete-button')} onClick={() => onClickDeleteAccount()}>
                Delete account
              </div>
            </section>
          </div>
        }
        backContent={
          <div className={`${bem('back')} ${bem('base')}`} style={{ visibility: isFlipped ? 'visible' : 'hidden' }}>
            <section
              className={`${bem('avatar-container')} ${bem('avatar-container', 'editable')}`}
              onClick={() => onChangeAvatarClicked()}
            >
              <Avatar
                className={bem('avatar')}
                rounded
                size={'2xxl'}
                imageUrl={newAvatarPreview ? newAvatarPreview : user?.avatarThumbMediumURL}
                loading={!user}
              />
              <i className="gik-icon-v2 icon-v2-image" />
            </section>

            {/*TODO: add a cancelButton prop to form and then we can use a full schema rather than hybrid form*/}
            <Form
              ref={formRef}
              variant="primary"
              schema={schema}
              vertical
              onSubmit={(values: EditingUserFormType) => saveUserInfo(values)}
              render={({ valid, submitting }) => {
                return (
                  <>
                    <FormField name={UserSettingsFormFieldNames.FirstName} />
                    <FormField name={UserSettingsFormFieldNames.LastName} />
                    <FormField name={UserSettingsFormFieldNames.EmailAddress} />
                    <FormField name={UserSettingsFormFieldNames.CityAndState} />
                    <br />
                    <Button fullWidth type="submit" variant="primary" loading={submitting} disabled={!valid}>
                      Save
                    </Button>

                    <Button fullWidth variant="default" onClick={() => cancelUserEditing()}>
                      Cancel
                    </Button>
                  </>
                );
              }}
            />
          </div>
        }
      />
    </Backdrop>
  );
}

export default withSuspense(UserSettings, UserSettingsSkeleton);
